import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UploadCloud, Loader2, Sparkles, ArrowLeft, Lock, Download, Mail, User, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getDeviceId } from "@/lib/device";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LockedPreview = ({ src, unlocked }) => {
  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden border border-white/10 select-none"
      data-testid="upscale-preview"
      onContextMenu={(e) => !unlocked && e.preventDefault()}
    >
      <img
        src={src}
        alt="4K"
        className="block w-full"
        draggable={unlocked}
        style={!unlocked ? { pointerEvents: "none" } : undefined}
      />

      {!unlocked && (
        <>
          {/* Blur the right 2/3 — only the first third stays sharp */}
          <div
            className="absolute inset-y-0 right-0 w-2/3 backdrop-blur-2xl bg-black/45 border-l border-dashed border-white/25 flex flex-col items-center justify-center gap-3 px-4 text-center"
            data-testid="upscale-locked-overlay"
          >
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-white/10 border border-white/15">
              <Lock className="h-6 w-6 text-white/90" />
            </span>
            <span className="text-white font-semibold text-base">Resultado 4K bloqueado</span>
            <span className="text-white/60 text-sm max-w-[220px]">Estás viendo solo 1/3 del iris. Desbloquea la imagen completa gratis.</span>
          </div>
          {/* Tiled watermark deterrent over the visible part */}
          <div className="absolute inset-y-0 left-0 w-1/3 pointer-events-none flex flex-wrap items-center justify-center gap-x-8 gap-y-6 opacity-[0.16] rotate-[-20deg] scale-125 overflow-hidden">
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} className="text-white font-bold text-sm whitespace-nowrap">Iris Photo</span>
            ))}
          </div>
          <span className="absolute top-3 left-3 z-[2] px-2.5 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-xs font-semibold">Vista previa · 1/3</span>
        </>
      )}

      {unlocked && (
        <span className="absolute top-3 right-3 z-[2] px-2.5 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-xs font-semibold">4K · IA · Desbloqueado</span>
      )}
    </div>
  );
};

export default function UpscalePage() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewName, setPreviewName] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const inputRef = useRef(null);
  const isAdmin = typeof window !== "undefined" && !!localStorage.getItem("admin_token");
  const fullAccess = unlocked || isAdmin;

  const reset = () => {
    setResult(null);
    setUnlocked(false);
    setEmail("");
    setName("");
    setPhone("");
  };

  const handleFile = async (file) => {
    if (!file) return;
    setPreviewName(file.name);
    setLoading(true);
    reset();
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/upscale`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success("¡Imagen escalada a 4K!");
    } catch (err) {
      toast.error("No se pudo escalar la imagen. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;
    setUnlocking(true);
    try {
      await axios.post(`${API}/upscale/unlock`, { name: name.trim(), email: email.trim(), phone: phone.trim() || null, device_id: getDeviceId() });
      setUnlocked(true);
      toast.success("¡Registro completado! Ya puedes descargar tu imagen en 4K.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error(err.response.data?.detail || "Ya has usado el desbloqueo gratuito desde este dispositivo.");
      } else {
        toast.error("Introduce un email válido para desbloquear.");
      }
    } finally {
      setUnlocking(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.upscaled_url) return;
    try {
      const res = await fetch(result.upscaled_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "iris-4k.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(result.upscaled_url, "_blank");
    }
  };

  return (
    <div className="min-h-screen hero-glow grain text-white" data-testid="upscale-page">
      <header className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="upscale-home-link">
          <img src="/logo.png" alt="Logo Iris Photo" className="h-9 w-9 rounded-xl object-cover" />
          <span className="text-lg font-semibold tracking-tight">Extracción Forense Iris Photo</span>
        </Link>
        <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Volver
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 pt-8 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-2xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            Escalado a <span className="text-gradient">4K con IA</span>
          </h1>
          <p className="mt-4 text-white/60 text-lg">Sube tu foto de iris y obtén una versión nítida en 4K en segundos. Verás una vista previa; desbloquea el resultado completo gratis.</p>
        </motion.div>

        <div className="mt-12 max-w-xl mx-auto">
          {!result && !loading && (
            <button
              data-testid="upscale-dropzone"
              onClick={() => inputRef.current?.click()}
              className="w-full glass rounded-3xl p-14 flex flex-col items-center gap-4 hover:border-indigo-400/40 transition-colors"
            >
              <span className="grid place-items-center h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20">
                <UploadCloud className="h-8 w-8 text-indigo-300" />
              </span>
              <span className="text-lg font-semibold">Sube tu imagen</span>
              <span className="text-white/50 text-sm">JPG, PNG o WEBP</span>
            </button>
          )}

          {loading && (
            <div className="w-full glass rounded-3xl p-16 flex flex-col items-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-indigo-400" />
              <span className="text-white/70">Escalando a 4K con IA… {previewName}</span>
            </div>
          )}

          {result && !loading && (
            <div className="flex flex-col gap-6">
              <LockedPreview src={result.upscaled_url} unlocked={fullAccess} />

              {!fullAccess ? (
                <motion.form
                  onSubmit={handleUnlock}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-2xl p-6 sm:p-8 flex flex-col gap-4"
                  data-testid="upscale-unlock-form"
                >
                  <div className="flex items-start gap-3">
                    <span className="grid place-items-center h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 shrink-0">
                      <Sparkles className="h-5 w-5 text-indigo-300" />
                    </span>
                    <div>
                      <p className="font-semibold text-lg">Regístrate para desbloquear tu 4K — gratis</p>
                      <p className="text-white/55 text-sm mt-0.5">Completa tus datos y te damos acceso a la imagen nítida y completa para descargar.</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                      <input
                        data-testid="upscale-name-input"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Tu nombre"
                        className="w-full h-12 pl-11 pr-4 rounded-full bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-indigo-400/60"
                      />
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                      <input
                        data-testid="upscale-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@email.com"
                        className="w-full h-12 pl-11 pr-4 rounded-full bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-indigo-400/60"
                      />
                    </div>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                      <input
                        data-testid="upscale-phone-input"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Teléfono (opcional)"
                        className="w-full h-12 pl-11 pr-4 rounded-full bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-indigo-400/60"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={unlocking}
                      className="rounded-full h-12 px-7 bg-gradient-to-r from-indigo-500 to-violet-500 hover:opacity-90 text-white gap-2"
                      data-testid="upscale-unlock-button"
                    >
                      {unlocking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
                      Registrarme y desbloquear 4K
                    </Button>
                  </div>
                  <p className="text-white/40 text-xs">Te enviaremos novedades y ofertas. Sin spam, cancela cuando quieras.</p>
                </motion.form>
              ) : (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button
                    onClick={handleDownload}
                    className="rounded-full h-12 px-7 bg-gradient-to-r from-indigo-500 to-violet-500 hover:opacity-90 text-white gap-2"
                    data-testid="upscale-download-button"
                  >
                    <Download className="h-5 w-5" /> Descargar 4K
                  </Button>
                  <Button
                    onClick={() => { reset(); inputRef.current?.click(); }}
                    variant="outline"
                    className="rounded-full h-12 px-7 bg-white/5 border-white/15 text-white hover:bg-white/10 gap-2"
                    data-testid="upscale-another-button"
                  >
                    <Sparkles className="h-5 w-5" /> Escalar otra
                  </Button>
                </div>
              )}
            </div>
          )}

          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} data-testid="upscale-file-input" />
        </div>
      </main>
    </div>
  );
}
